import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Upload for the Files app: posts one plain multipart request per file
// straight to the existing JSON `create` action (UserFilesController#create)
// via fetch + FormData - no Active Storage direct upload; see
// FILES_FEATURE_SPEC.md / Phase 3 instructions. Every file gets its own
// progress row, uploaded independently, so one rejected file (wrong type,
// too large, over quota) never blocks the rest.
//
// FILES_DESIGN_SPEC.md principle 1 ("the permanent dropzone is gone") moved
// drag-and-drop to files_app_controller.js's page-wide overlay - this
// controller no longer owns any dragover/drop handlers of its own, just the
// Upload button, the hidden file input, and the corner progress tray. It
// listens for the "files:dropped" event that controller fires instead.
export default class extends Controller {
    static targets = ["input", "tray", "trayTitle", "list"]
    static values = { url: String, folder: String }

    connect() {
        this._onDropped = (event) => this.upload(event.detail.files)
        document.addEventListener("files:dropped", this._onDropped)
    }

    disconnect() {
        document.removeEventListener("files:dropped", this._onDropped)
    }

    pick(event) {
        event.preventDefault()
        this.inputTarget.click()
    }

    filesChosen() {
        const files = this.inputTarget.files
        if (files && files.length) this.upload(files)
        // Reset so choosing the exact same file again still fires `change`.
        this.inputTarget.value = ""
    }

    dismissTray(event) {
        event?.preventDefault()
        if (this.hasTrayTarget) this.trayTarget.hidden = true
    }

    upload(fileList) {
        const files = Array.from(fileList)
        if (!files.length) return
        if (this.hasTrayTarget) this.trayTarget.hidden = false
        this._updateTrayTitle(files.length, 0)

        let doneCount = 0
        let anySucceeded = false
        const settled = files.map((file) =>
            this.uploadOne(file).then((ok) => {
                doneCount += 1
                if (ok) anySucceeded = true
                this._updateTrayTitle(files.length, doneCount)
            })
        )
        // Reload once every upload in the batch has settled, so the new
        // files, the refreshed quota bar and their extraction-status badges
        // all show up together rather than mid-batch.
        Promise.all(settled).then(() => {
            if (anySucceeded) window.setTimeout(() => window.location.reload(), 600)
        })
    }

    uploadOne(file) {
        const row = this.addRow(file.name)
        const formData = new FormData()
        formData.append("file", file)
        if (this.folderValue) formData.append("user_folder_id", this.folderValue)

        return fetch(this.urlValue, {
            method: "POST",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: formData
        })
            .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
            .then(({ data }) => {
                if (data && data.ok) {
                    this.markRow(row, "done", "Uploaded")
                    return true
                }
                this.markRow(row, "error", (data && data.error) || "Upload failed")
                return false
            })
            .catch(() => {
                this.markRow(row, "error", "Upload failed - check your connection")
                return false
            })
    }

    addRow(name) {
        const li = document.createElement("li")
        li.className = "files-upload-row"

        const nameEl = document.createElement("span")
        nameEl.className = "files-upload-name"
        nameEl.textContent = name

        const statusEl = document.createElement("span")
        statusEl.className = "files-upload-status"
        statusEl.textContent = "Uploading…"

        li.appendChild(nameEl)
        li.appendChild(statusEl)
        if (this.hasListTarget) this.listTarget.appendChild(li)
        return li
    }

    markRow(row, state, message) {
        row.classList.add(`files-upload-row--${state}`)
        row.querySelector(".files-upload-status").textContent = message
    }

    _updateTrayTitle(total, done) {
        if (!this.hasTrayTitleTarget) return
        this.trayTitleTarget.textContent = done >= total ? `Uploaded ${total} file${total === 1 ? "" : "s"}` : `Uploading ${total} file${total === 1 ? "" : "s"} — ${done} done`
    }
}
